import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Typography, message, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const { Title } = Typography;

interface Website {
  id: string;
  domain: string;
  title: string;
  description: string;
  keywords: string;
  updated_at: string;
}

const WebsiteList: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [websites, setWebsites] = useState<Website[]>([]);
  const navigate = useNavigate();

  const fetchWebsites = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/seo`);
      if (response.data.success) {
        setWebsites(response.data.data);
      } else {
        message.error(response.data.message || '获取网站列表失败');
      }
    } catch (error: any) {
      message.error('获取网站列表时出错: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWebsites();
  }, []);

  const handleDelete = async (id: string) => {
    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/api/seo/${id}`);
      if (response.data.success) {
        message.success('网站删除成功');
        fetchWebsites();
      } else {
        message.error(response.data.message || '删除网站失败');
      }
    } catch (error: any) {
      message.error('删除网站时出错: ' + (error.response?.data?.message || error.message));
    }
  };

  const columns = [
    {
      title: '域名',
      dataIndex: 'domain',
      key: 'domain',
      render: (text: string) => <a href={`http://${text}`} target="_blank" rel="noopener noreferrer">{text}</a>,
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '关键词',
      dataIndex: 'keywords',
      key: 'keywords',
      render: (text: string) => (
        <>
          {text.split(',').map((keyword, index) => (
            <Tag key={index} color="blue">{keyword.trim()}</Tag>
          ))}
        </>
      ),
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (text: string) => new Date(text).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Website) => (
        <Space size="middle" className="table-actions">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/websites/${record.id}/edit`)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除此网站信息吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="primary" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2}>网站管理</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/websites/new')}
        >
          添加网站
        </Button>
      </div>
      <Table
        loading={loading}
        columns={columns}
        dataSource={websites}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />
    </div>
  );
};

export default WebsiteList; 